// /t/{id}: an app template (RFC-052 "Web", byj-1wv.64.5) — what it is, the
// line that makes an app from it, its versions to download; and for its
// owner, its title and description, an invite link, the classes it is given
// to and the people who accepted one, and deleting it. A version is
// published from the project, by byj, never here: the page says the command.
// Laid out as the class pages are (byj-1wv.51.14): the head's actions one
// size, the line that makes an app always in view, and under it, for the
// owner, the versions and who may use it as two tabs, as a class's
// management keeps its parts; the versions, the people and the classes as
// rows, each person's and class's own action behind its ⋯, and deleting the
// template at the foot of who may use it. Anyone else has the versions alone,
// with no tab to pick.
import { useCallback, useEffect, useRef, useState } from "react";
import { api, appTemplateArchiveURL, type AppTemplateDetail, type AppTemplateVersion } from "../api";
import { useConfirm } from "../components/Confirm";
import { FormDialog } from "../components/FormDialog";
import { GiveToClass, useTeaching } from "../components/GiveToClass";
import { InviteLinkDialog, type InviteChoice } from "../components/InviteLink";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLoading, PageNotice } from "../components/Page";
import { InviteLinksChip, useOpenInvites } from "../components/OpenInvites";
import { RowMenu } from "../components/RowMenu";
import { EmptyRows, initialOf } from "../components/Rows";
import { panelId, tabId, Tabs } from "../components/Tabs";
import { useKeptChoice } from "../kept";
import { navigate } from "../router";
import { t, whenMinute, word, phrases } from "../i18n";
import { AuthorCommands } from "../components/CommandLine";
import { DocText } from "../components/DocText";
import { createCommand, PublishSteps } from "../components/TemplateParts";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** The calls a template's page makes, so a test can supply its own. */
export type TemplateApi = Pick<
  typeof api,
  | "appTemplate"
  | "describeAppTemplate"
  | "deleteAppTemplate"
  | "createAppTemplateInvite"
  | "openInvites"
  | "revokeInvite"
  | "replaceInvite"
  | "giveAppTemplate"
  | "takeAppTemplate"
  | "removeAppTemplateUser"
  | "classes"
>;

/** The id Edit names in aria-controls, so the dialog gives the focus back to it as it closes (FormDialog). */
const describeId = "template-describe";

/**
 * Its title and description, edited by its owner in a short dialog over the
 * page (FormDialog), as the Admin page's changes are: not in the page, where
 * the form pushed everything under it down, away from Edit in the head.
 * Saved, it closes; refused, it says why and stays open.
 */
function DescribeDialog({ detail, save, onClose }: {
  detail: AppTemplateDetail;
  save: (patch: { title: string; description: string }) => Promise<void>;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(detail.template.title);
  const [description, setDescription] = useState(detail.template.description);
  const changed = title.trim() !== detail.template.title.trim() || description.trim() !== detail.template.description.trim();
  return (
    <FormDialog
      id={describeId}
      testid={describeId}
      title={t("templates:templateEditTitle")}
      submitLabel={t("common:save")}
      canSubmit={changed}
      onSubmit={async () => {
        await save({ title: title.trim(), description: description.trim() });
        onClose();
      }}
      onClose={onClose}
    >
      <label className="field">
        {t("templates:templateTitleLabel")}
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder={detail.template.name} />
      </label>
      <label className="field">
        {t("templates:templateDescriptionLabel")}
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} maxLength={2000} />
      </label>
    </FormDialog>
  );
}

/** A version's row: its number, whether it is the latest, its hash, size and day, and its archive to download. */
function VersionRow({ id, v, latest }: { id: string; v: AppTemplateVersion; latest: boolean }) {
  return (
    <li className={`share-row no-menu version-row ${v.uploaded ? "is-live" : "is-ended"}`}>
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="4" width="16" height="5" rx="1" />
          <path d="M5.5 9v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V9M10 13h4" />
        </svg>
      </span>
      <div className="row-main">
        <span className="row-title">{t("templates:templateVersion", { n: v.n })}</span>
        <div className="row-sub">
          <code title={v.hash}>{v.hash.slice(0, 12)}</code> · {phrases.templateBytes(v.bytes)} · {whenMinute(v.created_at)}
        </div>
      </div>
      <div className="row-meta">
        {latest && <span className="chip chip-ok">{t("templates:templateLatest")}</span>}
        {v.uploaded ? (
          <a className="button small" href={appTemplateArchiveURL(id, v.n)} download>
            {t("templates:templateDownload", { n: v.n })}
          </a>
        ) : (
          // created, its archive never sent: its owner's alone to see (RFC-052 §5)
          <span className="chip chip-warn">{t("recordings:recordingNotUploaded")}</span>
        )}
      </div>
    </li>
  );
}

/** Its versions as rows, latest first. */
function Versions({ id, versions, latest }: { id: string; versions: AppTemplateVersion[]; latest: number }) {
  return (
    <ul className="rows" data-testid="template-versions">
      {versions.map((v) => (
        <VersionRow key={v.n} id={id} v={v} latest={v.n === latest} />
      ))}
    </ul>
  );
}

/** The owner's two tabs, the one picked kept across visits as a class's management keeps its own. */
type Tab = "versions" | "people";
const tabs: Tab[] = ["versions", "people"];
const useTab = () => useKeptChoice<Tab>("template-page.tab", "versions", tabs);

/** Who may use it besides its owner: invite, the people who accepted, the classes it is given to. */
function People({ id, detail, client, reload }: { id: string; detail: AppTemplateDetail; client: TemplateApi; reload: () => Promise<void> }) {
  // its open links beside Invite, and those made here, as a share's owner has them (byj-1wv.76)
  const open = useOpenInvites({ kind: "app-templates", id }, true, "", client);
  const [inviting, setInviting] = useState(false);
  const inviteButton = useRef<HTMLButtonElement>(null);
  const closeInvite = useCallback(() => {
    setInviting(false);
    inviteButton.current?.focus();
  }, []);
  const [choice, setChoice] = useState<InviteChoice>({ role: "user", uses: 1, ttl: phrases.ttlOptions()[2]![0] });
  const teaching = useTeaching(client.classes);
  const classesHead = useRef<HTMLHeadingElement>(null);
  const [error, setError] = useState<string | null>(null);
  const users = detail.users ?? [];
  const given = detail.classes ?? [];
  const givable = (teaching ?? []).filter((c) => !given.some((g) => g.id === c.id));
  /** A change, then the template as the relay has it after; true once both are done, false with the page saying why. */
  const act = async (f: () => Promise<unknown>): Promise<boolean> => {
    setError(null);
    try {
      await f();
      await reload();
      return true;
    } catch (err) {
      setError(errText(err));
      return false;
    }
  };
  return (
    <div className="stack template-audience" data-testid="template-people">
      <p className="muted small flush">{t("templates:templatePeopleNote")}</p>
      {inviting && (
        <InviteLinkDialog
          id={id}
          note={t("templates:templateInviteNote")}
          choice={choice}
          onChoice={setChoice}
          create={(c) => client.createAppTemplateInvite(id, c.uses, c.ttl)}
          made={open.made}
          onMade={open.add}
          onClose={closeInvite}
        />
      )}
      {error && <p className="error">{t("common:error", { m: error })}</p>}

      <div className="rows-panel">
        {/* each group's own way to add to it on its heading's line, as /shares' groups keep what acts on their rows */}
        <div className="rows-head-line" role="group" aria-label={t("templates:templateUsers")}>
          <h3 className="rows-head">
            {t("templates:templateUsers")} · {users.length}
          </h3>
          <button ref={inviteButton} className="small" aria-haspopup="dialog" aria-expanded={inviting} aria-controls={inviting ? `${id}-invite` : undefined} onClick={() => setInviting(true)}>
            {t("session:invite")}
          </button>
          <InviteLinksChip open={open} own />
        </div>
        {users.length === 0 ? (
          <EmptyRows text={t("templates:templateNoUsers")} />
        ) : (
          <ul className="rows" data-testid="template-users">
            {users.map((u) => (
              <li key={u.id} className="share-row member-row is-live">
                <span className="row-lead" />
                <span className="kind-box" aria-hidden="true">
                  {initialOf(u.name || u.email)}
                </span>
                <div className="row-main">
                  <span className="row-title">{u.name || u.email}</span>
                  {u.name && <div className="row-sub">{u.email}</div>}
                </div>
                <div className="row-meta" />
                <RowMenu
                  label={t("shares:moreFor", { title: u.name || u.email })}
                  items={[{ label: t("templates:templateRemoveUser"), onSelect: () => void act(() => client.removeAppTemplateUser(id, u.id)), danger: true }]}
                />
              </li>
            ))}
          </ul>
        )}

        <div className="rows-head-line" role="group" aria-label={t("templates:templateClasses")}>
          {/* focusable from code alone: where the focus goes once Give to a class has gone */}
          <h3 ref={classesHead} className="rows-head" tabIndex={-1}>
            {t("templates:templateClasses")} · {given.length}
          </h3>
          {/* a class they teach that does not have it yet, picked beside the list it joins;
              none left to give, no button (the rows say which have it) */}
          <GiveToClass
            givable={givable}
            note={t("templates:templateGiveNote")}
            give={(cls) => act(() => client.giveAppTemplate(id, cls))}
            focusAfterLast={() => classesHead.current?.focus()}
          />
        </div>
        {given.length === 0 ? (
          // nobody but an instructor gives a class anything: one known to teach none is told why there is no button
          <EmptyRows text={teaching?.length === 0 ? `${t("templates:templateNoClasses")} ${t("templates:templateNoClassTaught")}` : t("templates:templateNoClasses")} />
        ) : (
          <ul className="rows" data-testid="template-classes">
            {given.map((c) => (
              <li key={c.id} className="share-row class-list-row is-live">
                <span className="row-lead" />
                <span className="kind-box" aria-hidden="true">
                  {initialOf(c.name)}
                </span>
                <div className="row-main">
                  {/* the owner gives it only to a class they teach (RFC-052 §5): its page is theirs to open */}
                  <Link to={`/c/${encodeURIComponent(c.id)}`} className="row-title">
                    {c.name}
                  </Link>
                  <div className="row-sub">
                    <code>{t("classes:classCode", { code: c.code })}</code>
                  </div>
                </div>
                <div className="row-meta" />
                <RowMenu label={t("shares:moreFor", { title: c.name })} items={[{ label: t("templates:templateTake"), onSelect: () => void act(() => client.takeAppTemplate(id, c.id)) }]} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function TemplatePage({ id, client = api }: { id: string; client?: TemplateApi }) {
  const [detail, setDetail] = useState<AppTemplateDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useTab();
  const [ask, dialog] = useConfirm();
  const reload = useCallback(async () => {
    setDetail(await client.appTemplate(id));
  }, [client, id]);
  useEffect(() => {
    reload().catch((err: unknown) => setError(errText(err)));
  }, [reload]);

  if (error && !detail) {
    return (
      <PageNotice error={error}>
        <Link to="/templates">{t("templates:templatesTitle")}</Link>
      </PageNotice>
    );
  }
  if (!detail) return <PageLoading />;
  const tpl = detail.template;
  const owner = detail.role === "owner";
  // latest first; a version not uploaded yet is its owner's alone to see
  const versions = [...detail.versions].sort((a, b) => b.n - a.n);
  const substitute = detail.manifest?.substitute ?? [];
  const publishLine = `byj app publish --template ${tpl.name}`;
  // oldest first, as they are said
  const notUploaded = detail.versions.filter((v) => !v.uploaded).map((v) => v.n).sort((a, b) => a - b);

  const remove = async () => {
    if (!(await ask(t("templates:templateDeleteAsk", { name: tpl.name }), { action: t("templates:templateDelete"), danger: true }))) return;
    try {
      await client.deleteAppTemplate(tpl.id);
      navigate("/templates");
    } catch (err) {
      setError(errText(err));
    }
  };

  return (
    <Page className="template-page">
      <PageHead
        title={tpl.title || tpl.name}
        actions={
          <>
            {owner && (
              <button aria-haspopup="dialog" aria-expanded={editing} aria-controls={editing ? describeId : undefined} onClick={() => setEditing(true)}>
                {t("templates:templateEdit")}
              </button>
            )}
            {tpl.latest > 0 && (
              <a className="button" href={appTemplateArchiveURL(tpl.id, tpl.latest)} download>
                {t("templates:templateDownload", { n: tpl.latest })}
              </a>
            )}
            <Link to="/templates" className="button">
              {t("templates:templatesTitle")}
            </Link>
          </>
        }
      />
      {/* what it is, under its title: its name, its latest version, my standing toward it, whose it is */}
      <p className="row wrap small template-meta" data-testid="template-meta">
        <code>{tpl.name}</code>
        <span className={`chip${tpl.latest ? "" : " chip-warn"}`}>{tpl.latest ? t("templates:templateVersion", { n: tpl.latest }) : t("templates:templateNoVersion")}</span>
        <span className="chip">{word("templates:templateRole", detail.role) ?? detail.role}</span>
        {!owner && <span className="muted">{t("templates:templateBy", { owner: tpl.owner })}</span>}
      </p>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      <p className={tpl.description ? "pre-wrap" : "muted"} data-testid="template-description">
        {tpl.description || t("templates:templateNoDescription")}
      </p>
      {editing && (
        <DescribeDialog
          detail={detail}
          save={async (patch) => {
            const next = await client.describeAppTemplate(tpl.id, patch);
            setDetail((d) => (d ? { ...d, template: { ...d.template, ...next } } : d));
          }}
          onClose={() => setEditing(false)}
        />
      )}

      <section aria-labelledby="template-create" className="card pad stack template-create">
        <h2 id="template-create" className="flush">
          {t("templates:templateCreate")}
        </h2>
        {tpl.latest ? (
          <>
            <AuthorCommands commands={[createCommand(tpl)]} />
            {substitute.length > 0 && (
              <p className="muted small flush" data-testid="template-substitute">
                {t("templates:templateFilledIn")}{" "}
                {substitute.map((f) => (
                  <code key={f} className="file-name">
                    {f}
                  </code>
                ))}
              </p>
            )}
            <p className="muted small flush">{t("templates:templateCreateNote")}</p>
            {!tpl.mine && <p className="muted small flush">{t("templates:templateById")}</p>}
          </>
        ) : owner ? (
          // none published: how it is done, step by step, for its own name
          <>
            <p className="muted flush">{t("templates:templateNotPublished")}</p>
            <PublishSteps name={tpl.name} />
          </>
        ) : (
          <p className="muted flush">{t("templates:templateNotPublishedYet")}</p>
        )}
      </section>

      {owner ? (
        <>
          <Tabs
            prefix="template"
            label={t("templates:templateTabs")}
            value={tab}
            onChange={setTab}
            tabs={[
              { id: "versions", label: <>{t("templates:templateVersions")}<span className="tab-count">{versions.length}</span></> },
              {
                id: "people",
                label: <>{t("templates:templatePeople")}<span className="tab-count">{(detail.users?.length ?? 0) + (detail.classes?.length ?? 0)}</span></>,
              },
            ]}
          />
          <div role="tabpanel" id={panelId("template", "versions")} aria-labelledby={tabId("template", "versions")} hidden={tab !== "versions"} className="rows-panel">
            {/* a version created and never uploaded is its owner's alone to see: why, and what to do */}
            {notUploaded.length > 0 && (
              <p className="banner warn small" data-testid="template-not-uploaded">
                {phrases.templateNotUploadedNote(notUploaded)}
              </p>
            )}
            {versions.length === 0 ? <EmptyRows text={t("templates:templateNoVersions")} /> : <Versions id={tpl.id} versions={versions} latest={tpl.latest} />}
            {/* its next version is published from the project, by byj, never here */}
            {tpl.latest > 0 && (
              <div className="stack template-next" data-testid="template-publish">
                <p className="muted small flush">{t("templates:templatePublishNote")}</p>
                <AuthorCommands commands={[publishLine]} />
                <p className="muted small flush">
                  <DocText s={t("templates:templateNextVersionWhat")} />{" "}
                  <Link to="/docs#templates-publish">{t("templates:publishGuide")} →</Link>
                </p>
              </div>
            )}
          </div>
          <div role="tabpanel" id={panelId("template", "people")} aria-labelledby={tabId("template", "people")} hidden={tab !== "people"}>
            <People id={tpl.id} detail={detail} client={client} reload={reload} />
            {/* the template gone for good, after a question, at the foot of who
                may use it as a class's deletion is at the foot of its policy */}
            <section className="policy-group delete-class" aria-labelledby="delete-template-title">
              <h3 id="delete-template-title">{t("templates:templateDeleteTitle")}</h3>
              <p className="muted small flush">{t("templates:templateDeleteHelp")}</p>
              <div className="row">
                <button className="danger" onClick={() => void remove()}>
                  {t("templates:templateDelete")}
                </button>
              </div>
            </section>
          </div>
        </>
      ) : (
        // anyone else: the versions alone, no tab to pick
        versions.length > 0 && (
          <section aria-labelledby="template-versions" className="stack rows-panel">
            <h2 id="template-versions" className="flush">
              {t("templates:templateVersions")}
            </h2>
            <Versions id={tpl.id} versions={versions} latest={tpl.latest} />
          </section>
        )
      )}
      {dialog}
    </Page>
  );
}
